import { useEffect } from 'react';
import { usePrefsValue } from '@adiox/ui';
import type { Severity, SoundName } from './describe';
import {
  notifications,
  playsSound,
  wantsDesktop,
  type Notification,
  type SoundSetting,
} from './notificationStore';

/**
 * Short tones (frequency in Hz, length in ms), one per kind of event, like MT5's sounds: a position opening rises, a
 * profitable close is a bright two-note chime, a loss falls, a stop loss is a low double knock, a price alert rings three
 * times, and a danger (stop out) is a falling double tone that is hard to miss.
 */
const TONES: Record<SoundName, [number, number][]> = {
  open: [
    [523, 70],
    [659, 90],
  ],
  profit: [
    [784, 80],
    [1047, 140],
  ],
  loss: [
    [494, 90],
    [392, 140],
  ],
  stop: [
    [330, 110],
    [330, 110],
  ],
  order: [[698, 60]],
  alert: [
    [988, 90],
    [988, 90],
    [988, 90],
  ],
  message: [
    [659, 80],
    [880, 80],
    [659, 80],
  ],
  info: [[520, 90]],
  success: [
    [587, 80],
    [784, 110],
  ],
  warning: [
    [660, 140],
    [660, 140],
  ],
  danger: [
    [880, 160],
    [587, 160],
    [880, 160],
    [587, 220],
  ],
};

let audio: AudioContext | null = null;

/** Browsers only allow sound after the user has touched the page: the audio is set up on the first key or click. */
function unlockAudio() {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;
  audio ??= new Ctor();
  void audio.resume().catch(() => undefined);
}

export function playTone(sound: SoundName): void {
  if (!audio || audio.state !== 'running') return;
  let at = audio.currentTime;
  for (const [frequency, ms] of TONES[sound]) {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.15, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + ms / 1000);
    osc.connect(gain).connect(audio.destination);
    osc.start(at);
    osc.stop(at + ms / 1000 + 0.02);
    at += ms / 1000 + 0.04;
  }
}

function showDesktop(n: Notification) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const shown = new Notification(n.title, {
    body: n.text,
    tag: `adiox-${n.id}`,
    requireInteraction: n.severity === 'danger', // a stop out stays on screen until seen
  });
  shown.onclick = () => {
    window.focus();
    shown.close();
  };
}

/**
 * Makes new notifications heard and seen outside the page: a sound (by the user's setting; warnings and dangers by default)
 * and, when the terminal is in a background tab and the user allowed it, a desktop notification.
 */
export function useNotificationEffects(): void {
  const raw = usePrefsValue((s) => s.data.notifySound);
  const sound: SoundSetting = raw === 'off' || raw === 'all' ? raw : 'alerts';
  const desktop = usePrefsValue((s) => s.data.notifyDesktop === true);

  useEffect(() => {
    window.addEventListener('pointerdown', unlockAudio, true);
    window.addEventListener('keydown', unlockAudio, true);
    return () => {
      window.removeEventListener('pointerdown', unlockAudio, true);
      window.removeEventListener('keydown', unlockAudio, true);
    };
  }, []);

  useEffect(() => {
    // Only what happens from now on: a log restored after a reload must not ring again.
    const since = Date.now() - 1_000;
    let seen = notifications.getState().items[0]?.id ?? 0;
    return notifications.subscribe((state) => {
      const fresh = state.items.filter((n) => n.id > seen && n.at >= since);
      if (state.items[0]) seen = Math.max(seen, state.items[0].id);
      if (fresh.length === 0) return;
      // Several at once (a close-all, say): one sound, for the most serious of them.
      const order: Severity[] = ['info', 'success', 'warning', 'danger'];
      const worst = fresh.reduce((a, b) => (order.indexOf(b.severity) > order.indexOf(a.severity) ? b : a));
      if (playsSound(sound, worst.severity)) playTone(worst.sound ?? worst.severity);
      for (const n of fresh) {
        if (wantsDesktop(desktop, n.severity, document.visibilityState === 'hidden')) showDesktop(n);
      }
    });
  }, [sound, desktop]);
}
